// ============================================================================
// WhatsNewTourPage - cinematic "what's new" journey (fully client-side)
// ============================================================================
// Ad-style ghost-user walkthrough: a scripted cursor glides through replica
// app screens - registers for a tournament, picks a gauntlet, claims a
// reward, hits SPIN - and each click cuts to the live payoff (the real
// PokerTableSVG felt, the coach panel, the reels). The viewer never touches
// anything: BEGIN once, then it plays end to end (~63s). Everything is
// scripted locally - no server calls, no bots - so it scales to any
// audience and the drama is identical every run.
//
// Music: public/audio/whats-new.mp3 (also .mp4/.m4a/.wav/.ogg). The splash
// probes for the file and SHOWS its status; missing/mis-named files are
// named on screen. Starts on the BEGIN click, loops.
// ============================================================================
import { hideSlots } from '../utils/storeSafe';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PokerTableSVG from '../components/PokerTableSVG';
import PremiumThemedTableSVG from '../components/PremiumThemedTableSVG';
import { tutorialService } from '../services/tutorialService';
import { auth } from '../services/firebase';

// ─── Chapters ───────────────────────────────────────────────────────────────
type ChapterId = 'intro' | 'tables' | 'sophia' | 'training' | 'tournaments' | 'watch' | 'tilt' | 'rewards' | 'spin' | 'slots' | 'outro';
const CHAPTERS: Array<{ id: ChapterId; title: string; subtitle?: string; durationMs: number }> = [
  // Chapters sum to ~107s - the track loops for the whole tour and eases
  // itself out over the outro's first ~4 seconds regardless.
  { id: 'intro', title: "WHAT'S NEW", subtitle: 'Bigger tables. Smarter training. Real rewards.', durationMs: 4000 },
  { id: 'tables', title: 'NEW TABLES', subtitle: 'Every pot tells a story', durationMs: 21000 },
  { id: 'sophia', title: 'THE SOPHIA TABLE', subtitle: 'A premium room, dealt in style', durationMs: 9000 },
  { id: 'training', title: 'NEW TRAINING', subtitle: 'An AI coach in your corner', durationMs: 13000 },
  { id: 'tournaments', title: 'NEW TOURNAMENTS', subtitle: 'From first deal to final table', durationMs: 13000 },
  { id: 'watch', title: 'WATCH & CHEER', subtitle: 'Share a code - friends sweat every card with you', durationMs: 12000 },
  { id: 'tilt', title: 'THROW IT. SAY IT.', subtitle: 'Every player has a card - open it, then let them have it', durationMs: 16500 },
  { id: 'rewards', title: 'NEW REWARDS', subtitle: 'Playing pays', durationMs: 11000 },
  { id: 'spin', title: 'DAILY SPIN', subtitle: 'One free spin a day - PlayCoins, or real PSS Coins', durationMs: 12000 },
  ...(hideSlots() ? [] : [{ id: 'slots' as ChapterId, title: 'ARCADE', subtitle: 'Free-play games between sessions', durationMs: 10000 }]),
  { id: 'outro', title: 'YOUR SEAT IS WAITING', durationMs: 0 },
];

// Windows hides known extensions, so a file the user "named"
// whats-new.mp3 is often really whats-new.mp3.mp3 - probe the
// double-extension variants too so it Just Works.
const AUDIO_CANDIDATES = [
  // Checked FIRST: the current track lives at the root of public/ rather
  // than in public/audio/, and whichever candidate matches first is the one
  // that plays - so the newest track must lead the list.
  '/whats-new.mp3', '/whats-new.mp4', '/whats-new.m4a', '/whats-new.wav', '/whats-new.ogg',
  '/audio/whats-new.mp3', '/audio/whats-new.mp3.mp3',
  '/audio/whats-new.mp4', '/audio/whats-new.mp4.mp4', '/audio/whats-new.mp4.mp3',
  '/audio/whats-new.m4a', '/audio/whats-new.wav', '/audio/whats-new.ogg',
  // The current track as downloaded (no rename needed).
  '/audio/like-we-do-it-grace-mesa-no-copyright-music_GOc0YVDH.mp3',
  '/audio/like-we-do-it-grace-mesa-no-copyright-music_GOc0YVDH.mp3.mp3',
  '/audio/like-we-do-it-grace-mesa-no-copyright-music_GOc0YVDH.mp4',
  '/audio/like-we-do-it-grace-mesa-no-copyright-music_GOc0YVDH.m4a',
  '/audio/like-we-do-it-grace-mesa-no-copyright-music_GOc0YVDH.wav',
];

// App icon for the splash/intro lettering. Drop the icon at
// web-app/public/images/logo.png (any of these names work) and it appears
// automatically; absent, the lettering stands alone.
const LOGO_CANDIDATES = [
  '/images/logo.png', '/images/logo.svg', '/images/logo.webp',
  '/images/icon.png', '/logo.png', '/logo512.png', '/icon.png',
];

// ─── Scripted dream hand (NEW TABLES) ───────────────────────────────────────
const HERO = 'demo_hero';
const VILLAIN = 'demo_villain';
const card = (rank: string, suit: string) => ({ rank, suit });
const BASE_PLAYERS = [
  { id: HERO, name: 'You', chips: 8200, currentBet: 0, folded: false, allIn: false, holeCards: [card('A', 'spades'), card('A', 'diamonds')] },
  { id: 'demo_b1', name: 'Silicon Shark', chips: 6100, currentBet: 50, folded: false, allIn: false, holeCards: [] },
  { id: 'demo_b2', name: 'Byte Bluffer', chips: 4400, currentBet: 100, folded: false, allIn: false, holeCards: [] },
  { id: VILLAIN, name: 'The Predator', chips: 8150, currentBet: 0, folded: false, allIn: false, holeCards: [] },
  { id: 'demo_b4', name: 'Chip Munch', chips: 5900, currentBet: 0, folded: false, allIn: false, holeCards: [] },
  { id: 'demo_b5', name: 'RoboRaiser', chips: 7300, currentBet: 0, folded: false, allIn: false, holeCards: [] },
];
const BOARD = {
  flop: [card('K', 'clubs'), card('7', 'spades'), card('2', 'hearts')],
  turn: card('A', 'hearts'),
  river: card('4', 'clubs'),
};

interface DemoState {
  players: any[]; pot: number; communityCards: any[]; round: string;
  currentPlayerIndex: number; allInShowdown: boolean; allInOccurred: boolean;
  playerEquities: { [id: string]: number }; runoutPhase: string; winnerFlash: boolean;
  actionBar: 'hidden' | 'shown' | 'pressed';
}
const clone = (s: DemoState): DemoState => JSON.parse(JSON.stringify(s));
const initialDemo = (): DemoState => ({
  players: JSON.parse(JSON.stringify(BASE_PLAYERS)),
  pot: 150, communityCards: [], round: 'preflop', currentPlayerIndex: 3,
  allInShowdown: false, allInOccurred: false, playerEquities: {}, runoutPhase: 'none',
  winnerFlash: false, actionBar: 'hidden',
});

// The hand, with a ghost-user beat: when the villain jams, YOUR action bar
// appears and the cursor clicks CALL. Then the flip, the sweat, the ace.
const DEMO_STEPS: Array<{ at: number; apply: (s: DemoState) => void }> = [
  { at: 1500, apply: s => { const v = s.players[3]; v.currentBet = 300; v.chips -= 300; (v as any).lastAction = 'raise'; s.pot += 300; s.currentPlayerIndex = 4; } },
  { at: 2400, apply: s => { (s.players[4] as any).folded = true; (s.players[4] as any).lastAction = 'fold'; (s.players[5] as any).folded = true; (s.players[5] as any).lastAction = 'fold'; s.currentPlayerIndex = 0; } },
  { at: 3500, apply: s => { const h = s.players[0]; h.currentBet = 1100; h.chips -= 1100; (h as any).lastAction = 'raise'; s.pot += 1100; s.currentPlayerIndex = 1; } },
  { at: 4400, apply: s => { (s.players[1] as any).folded = true; (s.players[1] as any).lastAction = 'fold'; (s.players[2] as any).folded = true; (s.players[2] as any).lastAction = 'fold'; s.currentPlayerIndex = 3; } },
  { at: 5600, apply: s => { const v = s.players[3]; s.pot += v.chips; v.currentBet += v.chips; v.chips = 0; (v as any).allIn = true; (v as any).lastAction = 'all-in'; s.allInOccurred = true; s.currentPlayerIndex = 0; s.actionBar = 'shown'; } },
  { at: 7600, apply: s => { s.actionBar = 'pressed'; } },
  { at: 8000, apply: s => { const h = s.players[0]; s.pot += h.chips; h.currentBet += h.chips; h.chips = 0; (h as any).allIn = true; (h as any).lastAction = 'all-in'; s.actionBar = 'hidden'; } },
  { at: 9200, apply: s => { s.allInShowdown = true; s.players[3].holeCards = [card('K', 'hearts'), card('K', 'diamonds')]; s.playerEquities = { [HERO]: 81, [VILLAIN]: 19 }; s.currentPlayerIndex = -1; } },
  { at: 11000, apply: s => { s.round = 'flop'; s.runoutPhase = 'flop'; s.communityCards = [...BOARD.flop]; s.playerEquities = { [HERO]: 9, [VILLAIN]: 91 }; } },
  { at: 13200, apply: s => { s.round = 'turn'; s.runoutPhase = 'turn'; s.communityCards = [...BOARD.flop, BOARD.turn]; s.playerEquities = { [HERO]: 95, [VILLAIN]: 5 }; } },
  { at: 14800, apply: s => { s.round = 'river'; s.runoutPhase = 'complete'; s.communityCards = [...BOARD.flop, BOARD.turn, BOARD.river]; s.playerEquities = { [HERO]: 100, [VILLAIN]: 0 }; } },
  { at: 15600, apply: s => { s.round = 'ended'; s.winnerFlash = true; const h = s.players[0]; h.chips = s.pot; } },
];
const TABLES_CURSOR: CursorStep[] = [
  { at: 5900, x: 74, y: 84 },
  { at: 6700, x: 50, y: 90 },
  { at: 7600, x: 50, y: 90, click: true },
  { at: 8400, x: 50, y: 90, hide: true },
];

// ─── Training / final-table scene data ──────────────────────────────────────
const TRAINING_PLAYERS = [
  { id: HERO, name: 'You', chips: 5350, currentBet: 0, folded: false, allIn: false, holeCards: [card('Q', 'hearts'), card('Q', 'spades')] },
  { id: 't_b1', name: 'Coach Bot', chips: 4100, currentBet: 450, folded: false, allIn: false, holeCards: [], lastAction: 'raise' },
  { id: 't_b2', name: 'Grinder', chips: 3800, currentBet: 0, folded: true, allIn: false, holeCards: [], lastAction: 'fold' },
  { id: 't_b3', name: 'The Reg', chips: 6200, currentBet: 0, folded: true, allIn: false, holeCards: [], lastAction: 'fold' },
];
const TRAINING_BOARD = [card('J', 'spades'), card('8', 'diamonds'), card('3', 'clubs')];
const FINAL_PLAYERS = [
  { id: HERO, name: 'You', chips: 412000, currentBet: 16000, folded: false, allIn: false, holeCards: [card('A', 'clubs'), card('K', 'clubs')], lastAction: 'raise' },
  { id: 'f_b1', name: 'Silicon Shark', chips: 268000, currentBet: 8000, folded: false, allIn: false, holeCards: [] },
  { id: 'f_b2', name: 'The Predator', chips: 190000, currentBet: 0, folded: true, allIn: false, holeCards: [], lastAction: 'fold' },
];
const FINAL_BOARD = [card('10', 'clubs'), card('J', 'clubs'), card('4', 'hearts')];
const FINAL_FELT_INFO = [
  'PSS Sunday Major', 'Level 18: 4K/8K (1K)',
  'In the money: $1,250', 'Pay jump in 2: $1,750', 'My Rank: 1/3 | Paid: 8',
];

const REEL_SYMBOLS = ['7', 'A', '♦', 'K', '♠', 'Q', '♥', 'J', '♣'];

// ─── Sophia (premium) table showcase ────────────────────────────────────────
// Rendered as a SPECTATOR view (viewer id matches no seat): no hero-hand
// overlays, no card reveals - just the room, mid-hand, looking its best.
const SOPHIA_PLAYERS = [
  { id: 's_b0', name: 'Dax Romero', chips: 12400, currentBet: 600, folded: false, allIn: false, holeCards: [], lastAction: 'raise' },
  { id: 's_b1', name: 'Velvet Vega', chips: 9800, currentBet: 600, folded: false, allIn: false, holeCards: [], lastAction: 'call' },
  { id: 's_b2', name: 'Monaco Mike', chips: 7600, currentBet: 0, folded: true, allIn: false, holeCards: [], lastAction: 'fold' },
  { id: 's_b3', name: 'Lady Lux', chips: 15200, currentBet: 600, folded: false, allIn: false, holeCards: [], lastAction: 'call' },
];
const SOPHIA_BOARD = [card('K', 'spades'), card('9', 'diamonds'), card('5', 'clubs')];

// ─── Watch & cheer scene data ───────────────────────────────────────────────
const WATCH_CODE = 'PSS-4F7K';
const CHEER_MESSAGES = [
  { at: 6200, who: 'Mia', text: 'LET’S GOOO 🔥' },
  { at: 7400, who: 'Jake', text: 'call him. CALL HIM' },
  { at: 8600, who: 'Mia', text: 'one time!! 🙏' },
  { at: 10600, who: 'Jake', text: '👏👏👏 UNREAL' },
];
const WATCH_PLAYERS = [
  { id: HERO, name: 'You', chips: 0, currentBet: 5200, folded: false, allIn: true, holeCards: [card('A', 'hearts'), card('Q', 'hearts')], lastAction: 'all-in' },
  { id: 'w_b1', name: 'The Predator', chips: 0, currentBet: 5200, folded: false, allIn: true, holeCards: [card('J', 'clubs'), card('J', 'spades')], lastAction: 'all-in' },
  { id: 'w_b2', name: 'Chip Munch', chips: 6900, currentBet: 0, folded: true, allIn: false, holeCards: [], lastAction: 'fold' },
];
const WATCH_BOARD = {
  flop: [card('10', 'hearts'), card('7', 'hearts'), card('2', 'clubs')],
  turn: card('J', 'diamonds'),
  river: card('3', 'hearts'),
};

// ─── Live felt backdrop ─────────────────────────────────────────────────────
// Every slide gets live table action: card-only phases (intro, lobbies,
// pickers, outro) render this dimmed looping hand behind their content -
// board cycles preflop → river → winner and resets, pot swelling as it
// goes. Pure local state; costs nothing, never sits still.
const BD_BOARD = [card('9', 'spades'), card('6', 'hearts'), card('6', 'diamonds'), card('Q', 'clubs'), card('2', 'spades')];
const BD_STAGES: Array<{ cc: number; pot: number; acts: Array<string | null> }> = [
  { cc: 0, pot: 350, acts: ['raise', null, null, null] },
  { cc: 3, pot: 1100, acts: [null, 'call', null, 'raise'] },
  { cc: 4, pot: 2600, acts: [null, 'fold', 'call', null] },
  { cc: 5, pot: 5400, acts: ['all-in', null, 'call', null] },
  { cc: 5, pot: 0, acts: [null, null, null, null] },
];
const FeltBackdrop: React.FC = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1700);
    return () => clearInterval(iv);
  }, []);
  const st = BD_STAGES[tick % BD_STAGES.length];
  const players = [
    { id: 'bd_1', name: 'Ace Martinez', chips: 6400 - st.pot / 4, currentBet: 0, folded: false, allIn: false, holeCards: [], lastAction: st.acts[0] },
    { id: 'bd_2', name: 'Ruby Chen', chips: 5100, currentBet: 0, folded: st.acts[1] === 'fold', allIn: false, holeCards: [], lastAction: st.acts[1] },
    { id: 'bd_3', name: 'Dax Romero', chips: 7900 - st.pot / 4, currentBet: 0, folded: false, allIn: false, holeCards: [], lastAction: st.acts[2] },
    { id: 'bd_4', name: 'Lena Voss', chips: 4700, currentBet: 0, folded: false, allIn: st.acts[3] === 'all-in', holeCards: [], lastAction: st.acts[3] },
  ];
  return (
    <div className="wn-backdrop">
      <div className="wn-backdrop-felt">
        <PokerTableSVG
          players={players as any}
          currentPlayerIndex={-1}
          pot={st.pot}
          communityCards={BD_BOARD.slice(0, st.cc) as any}
          currentUserId="bd_none"
          round={st.cc === 0 ? 'preflop' : st.cc === 5 ? 'river' : 'turn'}
          maxSeats={4}
          disableCardFiltering
          turnTimeLeft={0}
        />
      </div>
    </div>
  );
};

// ─── Ghost cursor ───────────────────────────────────────────────────────────
interface CursorStep { at: number; x: number; y: number; click?: boolean; hide?: boolean }
const ScriptedCursor: React.FC<{ steps: CursorStep[] }> = ({ steps }) => {
  const [st, setSt] = useState<{ x: number; y: number; visible: boolean; click: boolean }>(
    { x: steps[0]?.x ?? 50, y: steps[0]?.y ?? 50, visible: false, click: false });
  useEffect(() => {
    const ts: Array<ReturnType<typeof setTimeout>> = [];
    for (const s of steps) {
      ts.push(setTimeout(() => setSt({ x: s.x, y: s.y, visible: !s.hide, click: !!s.click }), s.at));
      if (s.click) ts.push(setTimeout(() => setSt(p => ({ ...p, click: false })), s.at + 380));
    }
    return () => ts.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!st.visible) return null;
  return (
    <div className="wn-cursor" style={{ left: `${st.x}%`, top: `${st.y}%` }}>
      {st.click && <div className="wn-ripple" />}
      <svg width="26" height="30" viewBox="0 0 26 30" style={{ transform: st.click ? 'scale(0.82)' : 'scale(1)', transition: 'transform 140ms ease' }}>
        <path d="M2 2 L2 24 L8 19 L12 28 L16 26 L12 17 L20 16 Z"
          fill="#ffffff" stroke="#0b1220" strokeWidth="1.6" />
      </svg>
    </div>
  );
};

const WhatsNewTourPage: React.FC = () => {
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const [chapterIdx, setChapterIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const [demo, setDemo] = useState<DemoState>(initialDemo);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [audioStatus, setAudioStatus] = useState<'checking' | 'ready' | 'missing'>('checking');
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  // Brand beat: the app icon flashes center-screen wherever PokerSweepStars
  // gets "mentioned" (the PSS Sunday Major lobby, the PSS- watch code, the
  // outro) - scheduled per chapter in goToChapter.
  const [logoFlash, setLogoFlash] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const chapter = CHAPTERS[chapterIdx];

  // Probe for the music file by ACTUALLY LOADING IT, not by asking the
  // server about it. The old probe used fetch(HEAD) + a content-type check,
  // which works against a dev server but fails inside the iOS app: Capacitor
  // serves the bundle over its own scheme in WKWebView, where a HEAD request
  // to a local asset can be rejected or answered with no content-type. The
  // file was in the build the whole time - the probe just could not see it,
  // so the tour reported "no music found" on TestFlight while working on the
  // web. Letting an <audio> element resolve the metadata is the real test of
  // "can this actually play", and it behaves the same everywhere.
  useEffect(() => {
    let cancelled = false;

    const canPlay = (src: string) => new Promise<boolean>((resolve) => {
      const probe = new Audio();
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        probe.onloadedmetadata = null;
        probe.onerror = null;
        try { probe.src = ''; } catch { /* noop */ }
        resolve(ok);
      };
      probe.preload = 'metadata';
      probe.onloadedmetadata = () => finish(true);
      probe.onerror = () => finish(false);
      // Never hang the splash on a stalled request.
      setTimeout(() => finish(false), 3000);
      try { probe.src = src; probe.load(); } catch { finish(false); }
    });

    (async () => {
      for (const c of AUDIO_CANDIDATES) {
        if (cancelled) return;
        if (await canPlay(c)) {
          if (!cancelled) { setAudioSrc(c); setAudioStatus('ready'); }
          return;
        }
      }
      if (!cancelled) setAudioStatus('missing');
    })();
    return () => { cancelled = true; };
  }, []);

  // Probe for the app icon the same way (SPA fallback serves HTML for
  // missing files, so content-type is the real check).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const c of LOGO_CANDIDATES) {
        try {
          const r = await fetch(c, { method: 'HEAD' });
          const ct = r.headers.get('content-type') || '';
          if (r.ok && !ct.includes('text/html')) {
            if (!cancelled) setLogoSrc(c);
            return;
          }
        } catch { /* try next */ }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const clearTimers = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };

  const exitTour = useCallback(() => {
    clearTimers();
    try { localStorage.setItem('pss:whatsNewSeen:v1', '1'); } catch { /* noop */ }
    if (audioRef.current) audioRef.current.pause();
    navigate('/');
  }, [navigate]);

  const goToChapter = useCallback((idx: number) => {
    clearTimers();
    if (idx >= CHAPTERS.length) { exitTour(); return; }
    setChapterIdx(idx);
    const ch = CHAPTERS[idx];
    if (ch.id === 'tables') {
      setDemo(initialDemo());
      for (const step of DEMO_STEPS) {
        timersRef.current.push(setTimeout(() => {
          setDemo(prev => { const next = clone(prev); step.apply(next); return next; });
        }, step.at));
      }
    }
    // Logo flash on the brand beats of certain chapters.
    setLogoFlash(false);
    const flashAt: Partial<Record<ChapterId, number>> = { tournaments: 1100, watch: 3300, outro: 350 };
    const fa = flashAt[ch.id];
    if (fa != null) {
      timersRef.current.push(setTimeout(() => setLogoFlash(true), fa));
      timersRef.current.push(setTimeout(() => setLogoFlash(false), fa + 1500));
    }
    if (ch.durationMs > 0) {
      timersRef.current.push(setTimeout(() => goToChapter(idx + 1), ch.durationMs));
    }
  }, [exitTour]);

  // The track LOOPS for the whole tour, whatever its length - the OUTRO
  // slide is the fade cue: once the last slide is up, the music eases out
  // over ~4 seconds and stops. No more music dying mid-tour when the edit
  // is shorter than the chapters.
  const begin = () => {
    setStarted(true);
    const a = audioRef.current;
    // The track is a FULL song cut to the tour's length, so it plays once
    // through - no loop. The outro still fades it out over ~4s if the tour
    // reaches the last slide before the song ends.
    if (a && audioSrc) { a.volume = 0.85; a.loop = false; a.play().catch(() => {/* absent/blocked - silent */}); }
    goToChapter(0);
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !audioSrc) return;
    if (CHAPTERS[chapterIdx]?.id !== 'outro') return;
    // The outro is where people sit and read the buttons, so the track HOLDS
    // at full volume for a while before easing out - it used to start
    // fading the instant the last slide appeared and was gone in 4s.
    const OUTRO_HOLD_MS = 14_000;
    const OUTRO_FADE_MS = 10_000;
    const startVol = a.volume;
    const t0 = Date.now();
    const iv = setInterval(() => {
      const elapsed = Date.now() - t0;
      if (elapsed < OUTRO_HOLD_MS) return; // let it play
      const k = Math.min(1, (elapsed - OUTRO_HOLD_MS) / OUTRO_FADE_MS);
      a.volume = Math.max(0, startVol * (1 - k));
      if (k >= 1) { clearInterval(iv); a.pause(); }
    }, 100);
    return () => clearInterval(iv);
  }, [chapterIdx, audioSrc]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitTour();
      if (started && e.key === 'ArrowRight') goToChapter(Math.min(chapterIdx + 1, CHAPTERS.length - 1));
      if (started && e.key === 'ArrowLeft') goToChapter(Math.max(chapterIdx - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started, chapterIdx, goToChapter, exitTour]);

  useEffect(() => () => clearTimers(), []);
  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);

  const head = useMemo(() => (
    <div className="wn-chapterhead">
      <div className="wn-rule" />
      <div className="wn-title">{chapter.title}</div>
      {chapter.subtitle && <div className="wn-sub">{chapter.subtitle}</div>}
    </div>
  ), [chapter]);

  return (
    <div className="wn-root">
      <style>{STYLES}</style>
      <audio ref={audioRef} src={audioSrc || undefined} preload="auto" />
      <div className="wn-vignette" />

      {!started && (
        <div className="wn-center">
          {logoSrc && <img className="wn-logo" src={logoSrc} alt="" />}
          <div className="wn-rule" />
          <div className="wn-title wn-brandtitle">POKERSWEEPSTARS</div>
          <div className="wn-rule" style={{ animationDelay: '300ms' }} />
          <div className="wn-sub" style={{ marginTop: 18 }}>The all-new experience</div>
          <button className="wn-begin" onClick={begin}>BEGIN</button>
          {audioStatus === 'missing' && (
            <div className="wn-audiostatus missing">No music found - add web-app/public/audio/whats-new.mp3</div>
          )}
        </div>
      )}

      {started && chapter.id === 'intro' && <FeltBackdrop key="intro-bd" />}
      {started && chapter.id === 'intro' && (
        <div className="wn-center" key="intro">
          {logoSrc && <img className="wn-logo" src={logoSrc} alt="" />}
          <div className="wn-rule" />
          <div className="wn-title">{chapter.title}</div>
          <div className="wn-rule" style={{ animationDelay: '300ms' }} />
          {chapter.subtitle && <div className="wn-sub" style={{ marginTop: 18 }}>{chapter.subtitle}</div>}
        </div>
      )}

      {/* ── NEW TABLES: the dream hand, with the ghost user clicking CALL ── */}
      {started && chapter.id === 'tables' && (
        <div className="wn-stage" key="tables">
          {head}
          <div className="wn-tablewrap">
            <PokerTableSVG
              players={demo.players as any}
              currentPlayerIndex={demo.currentPlayerIndex}
              pot={demo.pot}
              communityCards={demo.communityCards as any}
              currentUserId={HERO}
              round={demo.round}
              maxSeats={6}
              disableCardFiltering
              allInShowdown={demo.allInShowdown}
              allInOccurred={demo.allInOccurred}
              playerEquities={demo.playerEquities}
              runoutPhase={demo.runoutPhase}
              turnTimeLeft={22}
            />
          </div>
          {demo.actionBar !== 'hidden' && (
            <div className="wn-actionbar">
              <button className="wn-act fold">FOLD</button>
              <button className={`wn-act call${demo.actionBar === 'pressed' ? ' pressed' : ''}`}>CALL 7,050</button>
            </div>
          )}
          {demo.winnerFlash && <div className="wn-winflash">+16,450</div>}
          <ScriptedCursor steps={TABLES_CURSOR} />
        </div>
      )}

      {started && chapter.id === 'sophia' && <SophiaScene key="sophia" head={head} />}
      {started && chapter.id === 'training' && <TrainingScene key="training" head={head} />}
      {started && chapter.id === 'tournaments' && <TournamentScene key="tournaments" head={head} />}
      {started && chapter.id === 'watch' && <WatchScene key="watch" head={head} />}
      {started && chapter.id === 'tilt' && <TiltScene key="tilt" head={head} />}
      {started && chapter.id === 'rewards' && <RewardsScene key="rewards" head={head} />}
      {started && chapter.id === 'spin' && <SpinScene key="spin" head={head} />}
      {started && chapter.id === 'slots' && <SlotsScene key="slots" head={head} />}

      {/* Brand beat: the icon flashes wherever PokerSweepStars is mentioned */}
      {started && logoFlash && logoSrc && (
        <img className="wn-logoflash" src={logoSrc} alt="" />
      )}

      {started && chapter.id === 'outro' && <FeltBackdrop key="outro-bd" />}
      {started && chapter.id === 'outro' && (
        <div className="wn-center" key="outro">
          {logoSrc && <img className="wn-logo" src={logoSrc} alt="" />}
          <div className="wn-rule" />
          <div className="wn-title">{chapter.title}</div>
          <div className="wn-rule" style={{ animationDelay: '300ms' }} />
          <div className="wn-ctas">
            <button className="wn-cta primary" onClick={() => { exitTour(); navigate('/tables'); }}>PLAY NOW</button>
            <button className="wn-cta" onClick={() => { exitTour(); navigate('/training'); }}>TRAIN</button>
            <button className="wn-cta" onClick={() => { exitTour(); navigate('/tournaments'); }}>TOURNAMENTS</button>
            {/* The full app walkthrough (same one Profile > App Tutorials
                launches): reset it so it plays from the start, then the
                ?startTutorial param triggers the overlay on the home page. */}
            <button className="wn-cta" onClick={async () => {
              try {
                const uid = auth.currentUser?.uid;
                if (uid) await tutorialService.resetTutorial(uid, 'main');
              } catch { /* reset is best-effort */ }
              exitTour();
              navigate('/?startTutorial=true');
            }}>APP TUTORIAL</button>
            <button className="wn-cta" onClick={() => {
              const a = audioRef.current;
              if (a && audioSrc) { a.currentTime = 0; a.volume = 0.85; a.play().catch(() => {/* noop */}); }
              goToChapter(0);
            }}>REWATCH</button>
          </div>
        </div>
      )}

      {started && (
        <>
          <div className="wn-dots">
            {CHAPTERS.map((c, i) => <div key={c.id} className={`wn-dot${i === chapterIdx ? ' on' : ''}`} />)}
          </div>
          <div className="wn-controls">
            <button className="wn-btn" onClick={() => setMuted(m => !m)}>{muted ? 'UNMUTE' : 'MUTE'}</button>
            {chapterIdx < CHAPTERS.length - 1 && (
              <button className="wn-btn" onClick={() => goToChapter(chapterIdx + 1)}>NEXT</button>
            )}
            <button className="wn-btn" onClick={exitTour}>EXIT</button>
          </div>
        </>
      )}
      {!started && (
        <div className="wn-controls"><button className="wn-btn" onClick={exitTour}>EXIT</button></div>
      )}
    </div>
  );
};

// ─── THE SOPHIA TABLE: premium felt glamour shot ───────────────────────────
const SophiaScene: React.FC<{ head: React.ReactNode }> = ({ head }) => {
  const [showCaption, setShowCaption] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowCaption(true), 2200);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="wn-stage">
      {head}
      <div className="wn-tablewrap wn-sophia" style={{ animationDelay: '0ms' }}>
        <PremiumThemedTableSVG
          gameState={{
            players: SOPHIA_PLAYERS,
            communityCards: SOPHIA_BOARD,
            pot: 2400,
            currentPlayerIndex: -1,
            round: 'flop',
            status: 'playing',
          } as any}
          currentUserId="wn_spectator"
          tableId="demo_sophia"
          tableName="Sophia's Room"
          maxSeats={6}
          voiceEnabled={false}
          soundEnabled={false}
        />
      </div>
      {showCaption && (
        <div className="wn-coach wn-sophiacaption">
          <div className="head">SOPHIA DEALS</div>
          <div className="tip">
            The premium room - live dealer presence, gifts, chat, and rabbit
            hunt, all on a felt built to be seen.
          </div>
          <div className="stat">NOW OPEN</div>
        </div>
      )}
    </div>
  );
};

// ─── WATCH & CHEER: share a watch code, cut to the friend railing you ──────
const WatchScene: React.FC<{ head: React.ReactNode }> = ({ head }) => {
  const [phase, setPhase] = useState<'share' | 'friend'>('share');
  const [codeShown, setCodeShown] = useState(false);
  const [board, setBoard] = useState<any[]>([...WATCH_BOARD.flop, WATCH_BOARD.turn]);
  const [runout, setRunout] = useState<'turn' | 'complete'>('turn');
  const [won, setWon] = useState(false);
  const [cheers, setCheers] = useState<Array<{ who: string; text: string }>>([]);
  useEffect(() => {
    const ts = [
      setTimeout(() => setCodeShown(true), 2700),
      setTimeout(() => setPhase('friend'), 5300),
      ...CHEER_MESSAGES.map(m => setTimeout(() => setCheers(c => [...c, m]), m.at)),
      setTimeout(() => { setBoard([...WATCH_BOARD.flop, WATCH_BOARD.turn, WATCH_BOARD.river]); setRunout('complete'); }, 9400),
      setTimeout(() => setWon(true), 10200),
    ];
    return () => ts.forEach(clearTimeout);
  }, []);
  return (
    <div className="wn-stage">
      {head}
      {phase === 'share' ? (
        <>
          <FeltBackdrop />
          <div className="wn-lobbycard">
            <div className="gtd">AT THE TABLE</div>
            <h2>Bring your rail</h2>
            <div className="rows">
              <div><span>They need</span><b>Just the code</b></div>
              <div><span>They see</span><b>Your table, live</b></div>
            </div>
            <button className={`wn-register watchbtn${codeShown ? ' done' : ''}`}>
              {codeShown ? 'CODE COPIED' : 'SHARE WATCH CODE'}
            </button>
            {codeShown && (
              <div className="wn-watchcode">{WATCH_CODE}</div>
            )}
          </div>
          <ScriptedCursor steps={[
            { at: 900, x: 30, y: 38 },
            { at: 1800, x: 50, y: 62 },
            { at: 2700, x: 50, y: 62, click: true },
            { at: 4600, x: 58, y: 72, hide: true },
          ]} />
        </>
      ) : (
        <>
          <div className="wn-tablewrap" style={{ animationDelay: '0ms' }}>
            <PokerTableSVG
              players={WATCH_PLAYERS as any}
              currentPlayerIndex={-1}
              pot={11450}
              communityCards={board as any}
              currentUserId={HERO}
              round={runout === 'complete' ? 'river' : 'turn'}
              maxSeats={3}
              disableCardFiltering
              allInShowdown
              allInOccurred
              playerEquities={runout === 'complete' ? { [HERO]: 100, w_b1: 0 } : { [HERO]: 32, w_b1: 68 }}
              runoutPhase={runout}
              turnTimeLeft={0}
            />
          </div>
          <div className="wn-watchbadge">● WATCHING LIVE · code {WATCH_CODE}</div>
          <div className="wn-cheerbox">
            {cheers.map((c, i) => (
              <div key={i} className="wn-cheer">
                <b>{c.who}</b> {c.text}
              </div>
            ))}
          </div>
          {won && <div className="wn-winflash">HEART FLUSH · +11,450</div>}
        </>
      )}
    </div>
  );
};

// ─── THROW IT. SAY IT.: player card → tomato arc → paid call-out ───────────
const TILT_PLAYERS = [
  { id: HERO, name: 'You', chips: 7900, currentBet: 0, folded: false, allIn: false, holeCards: [card('9', 'hearts'), card('9', 'clubs')] },
  { id: 'tt_b1', name: 'Silicon Shark', chips: 5200, currentBet: 0, folded: true, allIn: false, holeCards: [], lastAction: 'fold' },
  { id: 'tt_target', name: 'The Predator', chips: 9600, currentBet: 800, folded: false, allIn: false, holeCards: [], lastAction: 'raise' },
  { id: 'tt_b3', name: 'Ruby Chen', chips: 4400, currentBet: 0, folded: true, allIn: false, holeCards: [], lastAction: 'fold' },
];
const TILT_BOARD = [card('K', 'diamonds'), card('10', 'spades'), card('6', 'hearts')];

const TiltScene: React.FC<{ head: React.ReactNode }> = ({ head }) => {
  const [cardOpen, setCardOpen] = useState(false);
  const [throwing, setThrowing] = useState(false);
  const [impact, setImpact] = useState(false);
  const [shake, setShake] = useState(false);
  const [announce, setAnnounce] = useState(false);
  const [caption, setCaption] = useState(false);
  const [cardCaption, setCardCaption] = useState(false);
  // The PLAYER CARD is a headline feature in its own right, so it gets a
  // real beat here: it opens early, is captioned while it sits there, and
  // STAYS on screen through the throw (which is how it actually works -
  // you throw from the card). It used to flash by in 2.4s mid-chapter,
  // which is why it read as missing from the tour.
  useEffect(() => {
    const ts = [
      setTimeout(() => setCardOpen(true), 1500),
      setTimeout(() => setCardCaption(true), 2600),
      setTimeout(() => setCardCaption(false), 6400),
      setTimeout(() => setThrowing(true), 6600),
      setTimeout(() => { setThrowing(false); setImpact(true); setShake(true); }, 7480),
      setTimeout(() => { setShake(false); setCardOpen(false); }, 8100),
      setTimeout(() => setAnnounce(true), 8700),
      setTimeout(() => setAnnounce(false), 12400),
      setTimeout(() => setCaption(true), 12600),
    ];
    return () => ts.forEach(clearTimeout);
  }, []);
  return (
    <div className="wn-stage">
      {head}
      <div className="wn-tablewrap" style={{ animationDelay: '0ms' }}>
        <div className={`wn-shakewrap${shake ? ' on' : ''}`}>
          <PokerTableSVG
            players={TILT_PLAYERS as any}
            currentPlayerIndex={-1}
            pot={2050}
            communityCards={TILT_BOARD as any}
            currentUserId={HERO}
            round="flop"
            maxSeats={4}
            disableCardFiltering
            turnTimeLeft={0}
          />
        </div>
      </div>
      {cardOpen && (
        <div className="wn-profilecard">
          <div className="pc-top">
            <div className="pc-ava">🦈</div>
            <div>
              <div className="pc-name">The Predator</div>
              <div className="pc-stack">9,600 chips · 96 BB</div>
            </div>
            <div className="pc-tags">
              <span style={{ background: '#ef4444' }} />
              <span style={{ background: '#f59e0b' }} />
              <span style={{ background: '#3b82f6' }} />
            </div>
          </div>
          <div className="pc-stats">
            <div><span>VPIP</span><b>34%</b></div>
            <div><span>HANDS</span><b>412</b></div>
            <div><span>TOURNEYS</span><b>58</b></div>
          </div>
          <div className="pc-label">THROW SOMETHING</div>
          <div className="pc-row">
            <span className="pc-item hot">🍅</span>
            <span className="pc-item">💩</span>
            <span className="pc-item">🥚</span>
            <span className="pc-item">❄️</span>
          </div>
          <div className="pc-label">CALL-OUTS</div>
          <div className="pc-callout">📣 “Hey Predator, you are TRASH” · $0.50</div>
        </div>
      )}
      {cardCaption && (
        <div className="wn-cardcaption">
          <b>THE PLAYER CARD</b> — tap anyone at the table: live stack and
          big blinds, how they play, notes you've kept, colour tags that ring
          their seat… and everything you can throw at them.
        </div>
      )}
      {throwing && <div className="wn-proj">🍅</div>}
      {impact && (
        <div className="wn-splatwrap">
          <div className="wn-impactring" />
          <div className="wn-splat" />
          <div className="wn-splatdrip d1" />
          <div className="wn-splatdrip d2" />
          <div className="wn-splatdrip d3" />
        </div>
      )}
      {announce && (
        <div className="wn-announce">
          <div className="from">📣 PAID ANNOUNCEMENT FROM YOU</div>
          <div className="msg">HEY PREDATOR — YOU ARE TRASH 🗑️</div>
        </div>
      )}
      {caption && (
        <div className="wn-coach" style={{ animationDelay: '0ms' }}>
          <div className="head">TILT THE TABLE</div>
          <div className="tip">
            Tap any player to open their card - live stats, notes, and color
            tags - then let it fly: throwables that splat, free taunts, and
            paid table-wide call-outs.
          </div>
          <div className="stat">FREE TAUNTS · THROWS FROM $0.25</div>
        </div>
      )}
      <ScriptedCursor steps={[
        { at: 700, x: 50, y: 32 },
        { at: 1500, x: 50, y: 32, click: true },
        { at: 5200, x: 79, y: 54 },
        { at: 6500, x: 79, y: 54, click: true },
        { at: 6900, x: 79, y: 54, hide: true },
      ]} />
    </div>
  );
};

// ─── NEW TRAINING: pick a gauntlet → live decision spot with the coach ─────
const TrainingScene: React.FC<{ head: React.ReactNode }> = ({ head }) => {
  const [phase, setPhase] = useState<'picker' | 'table'>('picker');
  const [picked, setPicked] = useState(false);
  useEffect(() => {
    const ts = [
      setTimeout(() => setPicked(true), 2900),
      setTimeout(() => setPhase('table'), 4200),
    ];
    return () => ts.forEach(clearTimeout);
  }, []);
  return (
    <div className="wn-stage">
      {head}
      {phase === 'picker' ? (
        <>
          <FeltBackdrop />
          <div className="wn-scenegrid">
            <div className="wn-scenecard"><div className="tier">GAUNTLET</div><h3>Push / Fold Mastery</h3><p>Short-stack shoving ranges under pressure.</p><div className="earn">EARN 500 PC</div></div>
            <div className={`wn-scenecard${picked ? ' picked' : ''}`}><div className="tier">GAUNTLET</div><h3>The Squeeze</h3><p>Attack the raise-and-call with perfect timing.</p><div className="earn">EARN 750 PC</div></div>
            <div className="wn-scenecard"><div className="tier">GAUNTLET</div><h3>Final Table ICM</h3><p>Pay-jump math when every chip is a payout.</p><div className="earn">EARN 1,000 PC</div></div>
          </div>
          <ScriptedCursor steps={[
            { at: 800, x: 22, y: 40 },
            { at: 1900, x: 50, y: 52 },
            { at: 2900, x: 50, y: 52, click: true },
          ]} />
        </>
      ) : (
        <>
          <div className="wn-tablewrap" style={{ animationDelay: '0ms' }}>
            <PokerTableSVG
              players={TRAINING_PLAYERS as any}
              currentPlayerIndex={0}
              pot={1250}
              communityCards={TRAINING_BOARD as any}
              currentUserId={HERO}
              round="flop"
              maxSeats={4}
              disableCardFiltering
              turnTimeLeft={18}
            />
          </div>
          <div className="wn-coach" style={{ animationDelay: '900ms' }}>
            <div className="head">AI COACH</div>
            <div className="tip">Pocket Queens on J-8-3 rainbow - you're ahead of the raising range. Raise for value, don't just call.</div>
            <div className="stat">72% vs range</div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── NEW TOURNAMENTS: register in the lobby → cut to the final table ───────
const TournamentScene: React.FC<{ head: React.ReactNode }> = ({ head }) => {
  const [phase, setPhase] = useState<'lobby' | 'felt'>('lobby');
  const [registered, setRegistered] = useState(false);
  useEffect(() => {
    const ts = [
      setTimeout(() => setRegistered(true), 3000),
      setTimeout(() => setPhase('felt'), 5200),
    ];
    return () => ts.forEach(clearTimeout);
  }, []);
  return (
    <div className="wn-stage">
      {head}
      {phase === 'lobby' ? (
        <>
          <FeltBackdrop />
          <div className="wn-lobbycard">
            <div className="gtd">$10,000 GUARANTEED</div>
            <h2>PSS Sunday Major</h2>
            <div className="rows">
              <div><span>Buy-in</span><b>$50</b></div>
              <div><span>Starting stack</span><b>30,000</b></div>
              <div><span>Late reg</span><b>40 min</b></div>
              <div><span>Entrants</span><b>{registered ? '1,848' : '1,847'}</b></div>
            </div>
            <button className={`wn-register${registered ? ' done' : ''}`}>
              {registered ? 'REGISTERED' : 'REGISTER'}
            </button>
          </div>
          <ScriptedCursor steps={[
            { at: 900, x: 26, y: 34 },
            { at: 2000, x: 50, y: 67 },
            { at: 3000, x: 50, y: 67, click: true },
            { at: 4600, x: 58, y: 74, hide: true },
          ]} />
        </>
      ) : (
        <div className="wn-tablewrap" style={{ animationDelay: '0ms' }}>
          <PokerTableSVG
            players={FINAL_PLAYERS as any}
            currentPlayerIndex={1}
            pot={45000}
            communityCards={FINAL_BOARD as any}
            currentUserId={HERO}
            round="flop"
            maxSeats={3}
            disableCardFiltering
            turnTimeLeft={12}
            centerInfo={FINAL_FELT_INFO}
          />
        </div>
      )}
    </div>
  );
};

// ─── NEW REWARDS: complete the challenge, claim it, coins rain ─────────────
const RewardsScene: React.FC<{ head: React.ReactNode }> = ({ head }) => {
  const [progress, setProgress] = useState(2);
  const [claimed, setClaimed] = useState(false);
  useEffect(() => {
    const ts = [
      setTimeout(() => setProgress(3), 1400),
      setTimeout(() => setClaimed(true), 3400),
    ];
    return () => ts.forEach(clearTimeout);
  }, []);
  return (
    <div className="wn-stage">
      {head}
      <FeltBackdrop />
      {claimed && <ChipRain />}
      <div className={`wn-lobbycard reward${claimed ? ' claimed' : ''}`}>
        <div className="gtd">DAILY CHALLENGE</div>
        <h2>Win 3 Showdowns</h2>
        <div className="wn-progress">
          <div className="bar" style={{ width: `${(progress / 3) * 100}%` }} />
        </div>
        <div className="rows"><div><span>Progress</span><b>{progress} / 3</b></div></div>
        <button className={`wn-register${claimed ? ' done' : ''}`}>
          {claimed ? 'CLAIMED' : 'CLAIM 2,500 PC'}
        </button>
      </div>
      {claimed && <div className="wn-winflash" style={{ top: '30%' }}>+2,500 PC</div>}
      <ScriptedCursor steps={[
        { at: 1000, x: 30, y: 40 },
        { at: 2300, x: 50, y: 66 },
        { at: 3400, x: 50, y: 66, click: true },
        { at: 5200, x: 58, y: 74, hide: true },
      ]} />
    </div>
  );
};

// ─── DAILY SPIN: the ghost user takes the free spin, wheel lands on 2.5K ──
// Segment order and colours mirror the real wheel so the tour matches what
// players actually see. Geometry is simpler here than in the live component:
// a conic-gradient starts at 12 o'clock, so segment i covers
// [i*45, (i+1)*45] clockwise from the TOP - landing segment p under the
// pointer is just 360 - (p*45 + 22.5).
const SPIN_SEGMENTS = [
  { label: '100', color: '#3b82f6' },
  { label: '250', color: '#22c55e' },
  { label: '$1 PSS', color: '#06b6d4' },
  { label: '500', color: '#8b5cf6' },
  { label: '750', color: '#14b8a6' },
  { label: '1K', color: '#f59e0b' },
  { label: '3 PSS', color: '#0ea5e9' },
  { label: '2.5K', color: '#ec4899' },
  { label: '5K', color: '#ef4444' },
  { label: '25K', color: '#fbbf24' },
];
const SPIN_SEG_ANGLE = 360 / SPIN_SEGMENTS.length;
const SPIN_WIN_INDEX = 6; // $3 PSS - show off the real-money prize

const SpinScene: React.FC<{ head: React.ReactNode }> = ({ head }) => {
  const [rot, setRot] = useState(0);
  const [pressed, setPressed] = useState(false);
  const [won, setWon] = useState(false);
  useEffect(() => {
    const ts = [
      setTimeout(() => {
        setPressed(true);
        setRot(6 * 360 + (360 - (SPIN_WIN_INDEX * SPIN_SEG_ANGLE + SPIN_SEG_ANGLE / 2)));
      }, 2500),
      setTimeout(() => setWon(true), 6900),
    ];
    return () => ts.forEach(clearTimeout);
  }, []);

  const gradient = `conic-gradient(${SPIN_SEGMENTS.map((s, i) =>
    `${s.color} ${i * SPIN_SEG_ANGLE}deg ${(i + 1) * SPIN_SEG_ANGLE}deg`).join(', ')})`;

  return (
    <div className="wn-stage">
      {head}
      <FeltBackdrop />
      {won && <ChipRain />}
      <div className="wn-center" style={{ position: 'absolute' }}>
        <div className="wn-spinwrap">
          <div className="wn-spinpointer" />
          <div className="wn-spinwheel" style={{ background: gradient, transform: `rotate(${rot}deg)` }}>
            {SPIN_SEGMENTS.map((s, i) => (
              <div
                key={s.label}
                className="wn-spinlabel"
                style={{ transform: `rotate(${i * SPIN_SEG_ANGLE + SPIN_SEG_ANGLE / 2}deg)` }}
              >
                <span>{s.label}</span>
              </div>
            ))}
            <div className="wn-spinhub">🎁</div>
          </div>
        </div>
        <button className={`wn-register spinbtn${pressed ? ' done' : ''}`}>
          {pressed ? 'GOOD LUCK' : 'SPIN'}
        </button>
        {won && (
          <div className="wn-spinwin">
            You won <b>$3 PSS Coins</b>
            <span>Real, redeemable · plus PlayCoins on every other spin</span>
          </div>
        )}
      </div>
      <ScriptedCursor steps={[
        { at: 900, x: 66, y: 40 },
        { at: 1800, x: 50, y: 74 },
        { at: 2500, x: 50, y: 74, click: true },
        { at: 3400, x: 58, y: 80, hide: true },
      ]} />
    </div>
  );
};

// ─── NEW SLOTS: the ghost user hits SPIN, reels land 7-7-7 ─────────────────
const SlotsScene: React.FC<{ head: React.ReactNode }> = ({ head }) => {
  const [spinning, setSpinning] = useState(false);
  const [stopped, setStopped] = useState([false, false, false]);
  const [jackpot, setJackpot] = useState(false);
  useEffect(() => {
    const ts = [
      setTimeout(() => setSpinning(true), 2300),
      setTimeout(() => setStopped(s => [true, s[1], s[2]]), 4100),
      setTimeout(() => setStopped(s => [s[0], true, s[2]]), 4900),
      setTimeout(() => setStopped(s => [s[0], s[1], true]), 5700),
      setTimeout(() => setJackpot(true), 6100),
    ];
    return () => ts.forEach(clearTimeout);
  }, []);
  return (
    <div className="wn-stage">
      {head}
      <SlotsBackdrop />
      <div className="wn-center" style={{ position: 'absolute' }}>
        <div className="wn-slots" style={{ marginTop: '14vh' }}>
          {[0, 1, 2].map(i => (
            <div key={i} className={`wn-reel${stopped[i] ? ' stopped' : ''}${!spinning ? ' idle' : ''}`}>
              <div className="wn-strip">
                {(!spinning || stopped[i] ? [stopped[i] ? '7' : REEL_SYMBOLS[i * 2]] : [...REEL_SYMBOLS, ...REEL_SYMBOLS]).map((sym, j) => (
                  <div key={j} className="wn-sym">{sym}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button className={`wn-register spinbtn${spinning ? ' done' : ''}`}>
          {spinning ? 'SPINNING' : 'SPIN'}
        </button>
        {jackpot && <div className="wn-jackpot" style={{ animationDelay: '0ms' }}>BIG WIN</div>}
      </div>
      <ScriptedCursor steps={[
        { at: 700, x: 68, y: 44 },
        { at: 1500, x: 50, y: 64 },
        { at: 2300, x: 50, y: 64, click: true },
        { at: 3400, x: 58, y: 72, hide: true },
      ]} />
    </div>
  );
};

// ─── Slots backdrop ─────────────────────────────────────────────────────────
// NEW SLOTS gets the slot room's OWN ambiance - the gold-amber gradient the
// real slot games use (see config/slotGames.ts), pulsing glow, and a slow
// rain of machine symbols - not the poker felt.
const SLOT_BD_SYMBOLS = ['7', '♦', 'A', '♠', 'Q', '♥', 'K', '♣', '7', 'J'];
const SlotsBackdrop: React.FC = () => (
  <div className="wn-slotsbd">
    <div className="wn-slotsbd-glow g1" />
    <div className="wn-slotsbd-glow g2" />
    {SLOT_BD_SYMBOLS.map((s, i) => (
      <div
        key={i}
        className="wn-slotsbd-sym"
        style={{
          left: `${(7 + i * 97) % 100}%`,
          animationDuration: `${7 + ((i * 13) % 9)}s`,
          animationDelay: `${-((i * 17) % 20) / 2}s`,
          fontSize: `${26 + ((i * 11) % 34)}px`,
          opacity: 0.14 + ((i * 7) % 5) / 22,
        }}
      >{s}</div>
    ))}
  </div>
);

const ChipRain: React.FC = () => (
  <div className="wn-rain">
    {Array.from({ length: 18 }).map((_, i) => (
      <div key={i} className="wn-chip" style={{
        left: `${(i * 53) % 100}%`,
        animationDuration: `${2.4 + ((i * 7) % 10) / 6}s`,
        animationDelay: `${((i * 13) % 20) / 10}s`,
        width: `${20 + ((i * 5) % 14)}px`,
        height: `${20 + ((i * 5) % 14)}px`,
        opacity: 0.5 + ((i * 3) % 5) / 10,
      }} />
    ))}
  </div>
);

const STYLES = `
  .wn-root { position: fixed; inset: 0; z-index: 9600; background: #04070d; overflow: hidden;
    font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; color: #e2e8f0; }
  .wn-vignette { position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse at center, transparent 40%, rgba(2,4,8,0.9) 100%); }
  .wn-center { position: absolute; inset: 0; display: flex; flex-direction: column;
    align-items: center; justify-content: center; text-align: center; padding: 24px; }

  .wn-rule { width: min(60vw, 520px); height: 2px;
    background: linear-gradient(90deg, transparent, #d4af37 20%, #fff7d6 50%, #d4af37 80%, transparent);
    transform: scaleX(0); animation: wnRule 800ms cubic-bezier(0.16,1,0.3,1) 150ms forwards; }
  @keyframes wnRule { to { transform: scaleX(1); } }
  .wn-title { margin: 20px 0; font-family: Georgia, 'Times New Roman', serif; font-weight: 700;
    font-size: clamp(34px, 6.5vw, 74px); line-height: 1; white-space: nowrap; letter-spacing: 0.06em;
    background-image: linear-gradient(100deg, #8a6d1f 0%, #d4af37 22%, #fffbe8 38%, #d4af37 52%, #8a6d1f 74%, #d4af37 100%);
    background-size: 240% 100%; -webkit-background-clip: text; background-clip: text;
    -webkit-text-fill-color: transparent; color: transparent;
    filter: drop-shadow(0 3px 20px rgba(212,175,55,0.35));
    opacity: 0; transform: scale(1.3);
    animation: wnTitleIn 800ms cubic-bezier(0.16,1,0.3,1) 250ms forwards, wnSheen 2600ms linear 850ms infinite; }
  @keyframes wnTitleIn { to { opacity: 1; transform: scale(1); letter-spacing: 0.2em; } }
  @keyframes wnSheen { 0% { background-position: 120% 0; } 100% { background-position: -120% 0; } }
  .wn-sub { font-size: clamp(12px, 1.8vw, 17px); color: rgba(226,232,240,0.9);
    letter-spacing: 0.3em; text-transform: uppercase; opacity: 0; transform: translateY(10px);
    animation: wnSubIn 600ms ease 900ms forwards; }
  @keyframes wnSubIn { to { opacity: 1; transform: translateY(0); } }

  .wn-chapterhead { position: absolute; top: 3.5vh; left: 0; right: 0; text-align: center; pointer-events: none; z-index: 3; }
  .wn-chapterhead .wn-title { font-size: clamp(24px, 3.6vw, 42px); margin: 8px 0; }
  .wn-chapterhead .wn-rule { margin: 0 auto; width: min(38vw, 340px); }
  .wn-chapterhead .wn-sub { font-size: clamp(10px, 1.3vw, 13px); }

  .wn-stage { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    animation: wnStageIn 450ms ease; }
  @keyframes wnStageIn { from { opacity: 0; } to { opacity: 1; } }
  .wn-tablewrap { width: min(96vw, 1080px); height: min(70vh, 620px); margin-top: 7vh;
    opacity: 0; animation: wnFadeUp 700ms ease 250ms forwards, wnKenBurns 18s ease 900ms forwards; }
  @keyframes wnFadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
  /* Slow filmic push-in so no scene ever sits static */
  @keyframes wnKenBurns { from { scale: 1; } to { scale: 1.035; } }

  .wn-winflash { position: absolute; top: 46%; left: 0; right: 0; text-align: center; z-index: 4; pointer-events: none;
    font-family: Georgia, serif; font-size: clamp(30px, 5vw, 58px); font-weight: 700; letter-spacing: 0.12em;
    color: #6ee7b7; text-shadow: 0 0 40px rgba(52,211,153,0.8); animation: wnWin 650ms cubic-bezier(0.16,1,0.3,1); }
  @keyframes wnWin { from { opacity: 0; transform: scale(1.6); } to { opacity: 1; transform: scale(1); } }

  /* Ghost cursor */
  .wn-cursor { position: absolute; z-index: 8; pointer-events: none;
    transition: left 900ms cubic-bezier(0.3, 0, 0.2, 1), top 900ms cubic-bezier(0.3, 0, 0.2, 1);
    filter: drop-shadow(0 2px 8px rgba(0,0,0,0.7)); }
  .wn-ripple { position: absolute; left: -14px; top: -12px; width: 46px; height: 46px; border-radius: 50%;
    border: 2px solid rgba(212,175,55,0.9); animation: wnRipple 500ms ease-out forwards; }
  @keyframes wnRipple { from { transform: scale(0.35); opacity: 1; } to { transform: scale(1.5); opacity: 0; } }

  /* Hero action bar (tables chapter) */
  .wn-actionbar { position: absolute; bottom: 5vh; left: 50%; transform: translateX(-50%);
    display: flex; gap: 14px; z-index: 6; animation: wnFadeUp 350ms ease; }
  .wn-act { border-radius: 24px; padding: 14px 34px; font-weight: 800; font-size: 15px; letter-spacing: 0.08em;
    border: none; color: #fff; }
  .wn-act.fold { background: #7f1d1d; opacity: 0.85; }
  .wn-act.call { background: linear-gradient(135deg, #16a34a, #15803d); box-shadow: 0 6px 24px rgba(22,163,74,0.5); }
  .wn-act.call.pressed { transform: scale(0.94); box-shadow: 0 0 34px rgba(110,231,183,0.9); }

  /* Coach overlay */
  .wn-coach { position: absolute; right: max(4vw, 20px); top: 30%; width: min(300px, 42vw); z-index: 4;
    background: linear-gradient(160deg, rgba(13,31,46,0.97), rgba(6,14,24,0.97));
    border: 1px solid rgba(212,175,55,0.55); border-radius: 14px; padding: 18px;
    box-shadow: 0 18px 50px rgba(0,0,0,0.6); text-align: left;
    opacity: 0; transform: translateX(30px); animation: wnCoach 650ms cubic-bezier(0.16,1,0.3,1) forwards; }
  @keyframes wnCoach { to { opacity: 1; transform: translateX(0); } }
  .wn-coach .head { font-family: Georgia, serif; color: #d4af37; font-size: 13px; letter-spacing: 0.24em; margin-bottom: 8px; }
  .wn-coach .tip { font-size: 13.5px; line-height: 1.55; color: #dbe4ee; }
  .wn-coach .stat { margin-top: 10px; display: inline-block; background: rgba(52,211,153,0.12);
    border: 1px solid rgba(52,211,153,0.5); color: #6ee7b7; border-radius: 8px;
    padding: 4px 10px; font-size: 12px; font-weight: 700; letter-spacing: 0.08em; }

  /* Scene cards (training picker) */
  .wn-scenegrid { display: flex; gap: 18px; margin-top: 12vh; flex-wrap: wrap; justify-content: center; max-width: 1040px; }
  .wn-scenecard { width: min(86vw, 280px); background: linear-gradient(160deg, rgba(13,31,46,0.96), rgba(6,14,24,0.96));
    border: 1px solid rgba(148,163,184,0.3); border-radius: 14px; padding: 22px; text-align: left;
    opacity: 0; transform: translateY(26px); box-shadow: 0 18px 50px rgba(0,0,0,0.5);
    transition: border-color 300ms ease, transform 300ms ease, box-shadow 300ms ease; }
  .wn-scenecard:nth-child(1) { animation: wnCard 550ms cubic-bezier(0.16,1,0.3,1) 500ms forwards; }
  .wn-scenecard:nth-child(2) { animation: wnCard 550ms cubic-bezier(0.16,1,0.3,1) 750ms forwards; }
  .wn-scenecard:nth-child(3) { animation: wnCard 550ms cubic-bezier(0.16,1,0.3,1) 1000ms forwards; }
  @keyframes wnCard { to { opacity: 1; transform: translateY(0); } }
  .wn-scenecard.picked { border-color: #d4af37; transform: translateY(-6px) scale(1.04) !important;
    box-shadow: 0 22px 60px rgba(212,175,55,0.35); }
  .wn-scenecard .tier { font-size: 10px; letter-spacing: 0.3em; color: #67e8f9; margin-bottom: 8px; }
  .wn-scenecard h3 { margin: 0 0 7px 0; font-family: Georgia, serif; color: #fff; font-size: 19px; }
  .wn-scenecard p { margin: 0 0 12px 0; font-size: 12.5px; line-height: 1.5; color: #94a3b8; }
  .wn-scenecard .earn { display: inline-block; font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
    color: #d4af37; border: 1px solid rgba(212,175,55,0.5); border-radius: 8px; padding: 4px 10px; }

  /* Lobby / challenge card */
  .wn-lobbycard { width: min(90vw, 420px); margin-top: 10vh; text-align: center; z-index: 2;
    background: linear-gradient(160deg, rgba(13,31,46,0.97), rgba(6,14,24,0.97));
    border: 1px solid rgba(212,175,55,0.45); border-radius: 18px; padding: 30px 30px 26px;
    box-shadow: 0 24px 70px rgba(0,0,0,0.6);
    opacity: 0; animation: wnFadeUp 600ms ease 350ms forwards; }
  .wn-lobbycard .gtd { font-size: 11px; letter-spacing: 0.34em; color: #d4af37; margin-bottom: 10px; }
  .wn-lobbycard h2 { margin: 0 0 18px 0; font-family: Georgia, serif; font-size: 27px; color: #fff; letter-spacing: 0.03em; }
  .wn-lobbycard .rows { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }
  .wn-lobbycard .rows div { display: flex; justify-content: space-between; font-size: 13.5px;
    background: rgba(255,255,255,0.04); border-radius: 8px; padding: 8px 14px; }
  .wn-lobbycard .rows span { color: #8fa1b5; }
  .wn-lobbycard .rows b { color: #fff; }
  .wn-lobbycard.claimed { border-color: rgba(52,211,153,0.6); }
  .wn-register { width: 100%; border: none; border-radius: 24px; padding: 15px 0; font-size: 15px;
    font-weight: 800; letter-spacing: 0.2em; cursor: default; color: #181205;
    background: linear-gradient(135deg, #f5d976, #d4af37 55%, #a97f1f);
    box-shadow: 0 8px 28px rgba(212,175,55,0.45); transition: all 250ms ease; }
  .wn-register.done { background: linear-gradient(135deg, #34d399, #059669); color: #04120c;
    box-shadow: 0 8px 30px rgba(52,211,153,0.5); }
  .wn-register.spinbtn { width: auto; padding: 15px 60px; margin-top: 26px; }
  .wn-progress { height: 10px; border-radius: 6px; background: rgba(255,255,255,0.08); overflow: hidden; margin-bottom: 12px; }
  .wn-progress .bar { height: 100%; border-radius: 6px;
    background: linear-gradient(90deg, #d4af37, #fde68a); transition: width 900ms cubic-bezier(0.16,1,0.3,1); }

  /* Chip rain */
  .wn-rain { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
  .wn-chip { position: absolute; top: -60px; border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #fff2c0, #d4af37 55%, #8a6d1f);
    box-shadow: 0 0 12px rgba(212,175,55,0.6); animation: wnDrop linear infinite; }
  @keyframes wnDrop { from { transform: translateY(-8vh) rotate(0deg); } to { transform: translateY(115vh) rotate(720deg); } }

  /* Daily Spin wheel */
  .wn-spinwrap { position: relative; width: min(62vw, 330px); height: min(62vw, 330px);
    margin-top: 12vh; }
  .wn-spinwheel { position: absolute; inset: 0; border-radius: 50%;
    border: 6px solid #fbbf24;
    box-shadow: 0 0 0 4px #1a1a2e, 0 0 46px rgba(251,191,36,0.45), inset 0 0 26px rgba(0,0,0,0.55);
    transition: transform 4.2s cubic-bezier(0.17, 0.67, 0.12, 0.99); }
  .wn-spinpointer { position: absolute; top: -14px; left: 50%; z-index: 4;
    transform: translateX(-50%);
    width: 0; height: 0; border-left: 13px solid transparent;
    border-right: 13px solid transparent; border-top: 22px solid #fbbf24;
    filter: drop-shadow(0 3px 5px rgba(0,0,0,0.6)); }
  /* Each label is a full-height strip rotated about the centre, so its text
     sits on that segment's mid-line. */
  .wn-spinlabel { position: absolute; inset: 0; display: flex; justify-content: center;
    align-items: flex-start; pointer-events: none; }
  .wn-spinlabel span { margin-top: 9%; font-family: Georgia, serif; font-weight: 700;
    font-size: clamp(11px, 2.1vw, 16px); color: #fff; letter-spacing: 0.04em;
    text-shadow: 0 2px 5px rgba(0,0,0,0.65); }
  .wn-spinhub { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
    width: 27%; height: 27%; border-radius: 50%; background: #fbbf24;
    border: 5px solid #1a1a2e; display: flex; align-items: center; justify-content: center;
    font-size: clamp(20px, 4vw, 32px); }
  .wn-spinwin { margin-top: 22px; text-align: center; font-size: clamp(14px, 2.4vw, 19px);
    color: #dbe4ee; opacity: 0; animation: wnFadeUp 550ms ease forwards; }
  .wn-spinwin b { display: block; font-family: Georgia, serif; color: #6ee7b7;
    font-size: clamp(24px, 4.6vw, 40px); text-shadow: 0 0 30px rgba(52,211,153,0.6);
    margin: 4px 0 6px; }
  .wn-spinwin span { display: block; font-size: clamp(10px, 1.7vw, 13px); color: #94a3b8;
    letter-spacing: 0.1em; }

  /* Slots */
  .wn-slots { display: flex; gap: 14px; }
  .wn-reel { width: min(20vw, 118px); height: 148px; overflow: hidden; border-radius: 12px;
    background: linear-gradient(180deg, #0a1420, #101f31 50%, #0a1420);
    border: 2px solid rgba(212,175,55,0.5); box-shadow: inset 0 8px 24px rgba(0,0,0,0.7); position: relative; }
  .wn-strip { display: flex; flex-direction: column; animation: wnSpin 220ms linear infinite; }
  .wn-reel:nth-child(1) .wn-strip { animation-duration: 190ms; }
  .wn-reel:nth-child(2) .wn-strip { animation-duration: 230ms; }
  .wn-reel:nth-child(3) .wn-strip { animation-duration: 270ms; }
  .wn-reel.stopped .wn-strip, .wn-reel.idle .wn-strip { animation: none; transform: translateY(0); }
  .wn-sym { height: 148px; display: flex; align-items: center; justify-content: center;
    font-family: Georgia, serif; font-size: 62px; font-weight: 700; color: #cbd5e0; flex-shrink: 0; }
  .wn-reel.stopped .wn-sym { color: #d4af37; text-shadow: 0 0 26px rgba(212,175,55,0.8); animation: wnSymPop 450ms ease; }
  @keyframes wnSpin { from { transform: translateY(0); } to { transform: translateY(-444px); } }
  @keyframes wnSymPop { 0% { transform: scale(0.7); } 60% { transform: scale(1.15); } 100% { transform: scale(1); } }
  .wn-jackpot { margin-top: 24px; font-family: Georgia, serif; font-size: clamp(26px, 4vw, 44px);
    letter-spacing: 0.3em; color: #fde68a; text-shadow: 0 0 34px rgba(212,175,55,0.8);
    opacity: 0; animation: wnJack 550ms ease forwards; }
  @keyframes wnJack { from { opacity: 0; transform: scale(1.4); } to { opacity: 1; transform: scale(1); } }

  .wn-controls { position: absolute; bottom: 18px; right: 22px; display: flex; gap: 10px; z-index: 9; }
  .wn-btn { background: rgba(15,23,42,0.8); border: 1px solid rgba(148,163,184,0.4); color: #cbd5e0;
    border-radius: 18px; padding: 8px 16px; font-size: 12px; font-weight: 700; letter-spacing: 0.08em; cursor: pointer; }
  .wn-btn:hover { border-color: #d4af37; color: #fde68a; }
  .wn-dots { position: absolute; bottom: 26px; left: 0; right: 0; display: flex; gap: 8px; justify-content: center; z-index: 9; }
  .wn-dot { width: 7px; height: 7px; border-radius: 50%; background: rgba(148,163,184,0.35); }
  .wn-dot.on { background: #d4af37; box-shadow: 0 0 8px rgba(212,175,55,0.8); }

  /* Live felt backdrop - dimmed looping hand behind card-only slides */
  .wn-backdrop { position: absolute; inset: 0; z-index: 0; pointer-events: none;
    display: flex; align-items: center; justify-content: center; overflow: hidden;
    animation: wnStageIn 700ms ease; }
  .wn-backdrop-felt { width: min(110vw, 1240px); height: min(78vh, 700px);
    filter: blur(2.5px) brightness(0.38) saturate(0.85); transform: scale(1.08);
    animation: wnKenBurns 22s ease infinite alternate; }

  /* Slots backdrop - the slot room's own ambiance behind NEW SLOTS */
  .wn-slotsbd { position: absolute; inset: 0; z-index: 0; pointer-events: none; overflow: hidden;
    background: linear-gradient(180deg, #1a0f00 0%, #2d1a00 30%, #120a00 70%, #1a0f00 100%);
    animation: wnStageIn 700ms ease; }
  .wn-slotsbd-glow { position: absolute; border-radius: 50%; filter: blur(70px); }
  .wn-slotsbd-glow.g1 { width: 46vw; height: 46vw; left: 8vw; top: 6vh;
    background: radial-gradient(circle, rgba(212,175,55,0.55), transparent 70%);
    animation: wnSlotsGlow 6s ease-in-out infinite alternate; }
  .wn-slotsbd-glow.g2 { width: 38vw; height: 38vw; right: 4vw; bottom: 4vh;
    background: radial-gradient(circle, rgba(168,85,247,0.4), transparent 70%);
    animation: wnSlotsGlow 7.5s ease-in-out 1s infinite alternate; }
  @keyframes wnSlotsGlow { from { transform: scale(1); opacity: 0.22; } to { transform: scale(1.18); opacity: 0.45; } }
  .wn-slotsbd-sym { position: absolute; top: -8vh; font-family: Georgia, serif; font-weight: 700;
    color: #d4af37; text-shadow: 0 0 18px rgba(212,175,55,0.5);
    animation-name: wnSymFall; animation-timing-function: linear; animation-iteration-count: infinite; }
  @keyframes wnSymFall { from { transform: translateY(-10vh) rotate(-12deg); }
    to { transform: translateY(120vh) rotate(14deg); } }

  /* Brand flash - the icon pops center-screen on PokerSweepStars mentions */
  .wn-logoflash { position: absolute; left: 50%; top: 42%; z-index: 7; pointer-events: none;
    width: clamp(110px, 16vw, 190px); height: clamp(110px, 16vw, 190px); object-fit: contain;
    border-radius: 22%; transform: translate(-50%, -50%);
    filter: drop-shadow(0 0 60px rgba(212,175,55,0.75));
    animation: wnLogoFlash 1500ms cubic-bezier(0.16,1,0.3,1) forwards; }
  @keyframes wnLogoFlash {
    0% { opacity: 0; transform: translate(-50%, -50%) scale(0.45); }
    22% { opacity: 1; transform: translate(-50%, -50%) scale(1.06); }
    38% { transform: translate(-50%, -50%) scale(1); }
    72% { opacity: 1; }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(1.18); } }

  /* Sophia premium showcase - a live component in a demo frame: scale it
     to fit (outer seats included), hide its interactive chrome (Invite,
     gift/chat buttons), and let nothing steal the click-through. */
  .wn-sophia { transform: scale(0.82); transform-origin: center 32%; pointer-events: none; }
  .wn-sophia svg { max-height: 100% !important; width: auto; margin: 0 auto; display: block; }
  .wn-sophia button { display: none !important; }
  /* The dealer image is sized by VIEWPORT media queries (600px wide on a
     big monitor - proportioned for the app's full-screen 1400px table).
     Inside the demo's ~1000px frame that planted Sophia on top of the
     board. Size her to THIS frame and tuck her behind the top rail. */
  .wn-sophia .premium-table-wrapper { min-height: 0 !important; padding-top: 0 !important; }
  .wn-sophia .premium-dealer-container { top: -10px !important; }
  .wn-sophia .premium-dealer-image,
  .wn-sophia .premium-dealer-loading,
  .wn-sophia .premium-dealer-fallback {
    width: 290px !important; max-height: 320px !important; margin-bottom: -84px !important;
  }
  .wn-sophia .premium-table-svg { max-width: 100% !important; }
  .wn-sophiacaption { top: 56%; }

  /* Watch & cheer */
  .wn-watchcode { margin-top: 16px; font-family: ui-monospace, monospace; font-size: 30px; font-weight: 800;
    letter-spacing: 0.34em; color: #fde68a; text-shadow: 0 0 26px rgba(212,175,55,0.7);
    animation: wnJack 500ms ease forwards; }
  .wn-register.watchbtn { background: linear-gradient(135deg, #a78bfa, #7c3aed 60%, #5b21b6); color: #fff;
    box-shadow: 0 8px 28px rgba(124,58,237,0.5); }
  .wn-register.watchbtn.done { background: linear-gradient(135deg, #34d399, #059669); color: #04120c; }
  .wn-watchbadge { position: absolute; top: 14vh; left: 50%; transform: translateX(-50%); z-index: 5;
    background: rgba(10,14,22,0.92); border: 1px solid rgba(248,113,113,0.6); color: #fca5a5;
    border-radius: 16px; padding: 7px 18px; font-size: 12.5px; font-weight: 800; letter-spacing: 0.12em;
    animation: wnFadeUp 450ms ease; }
  .wn-cheerbox { position: absolute; left: max(3vw, 16px); bottom: 9vh; z-index: 6; display: flex;
    flex-direction: column; gap: 8px; max-width: min(46vw, 320px); }
  .wn-cheer { background: rgba(13,31,46,0.95); border: 1px solid rgba(103,232,249,0.4);
    border-radius: 14px 14px 14px 4px; padding: 9px 14px; font-size: 13.5px; color: #dbe4ee;
    box-shadow: 0 10px 30px rgba(0,0,0,0.5); opacity: 0; transform: translateX(-24px) scale(0.9);
    animation: wnCheerIn 420ms cubic-bezier(0.16,1,0.3,1) forwards; }
  .wn-cheer b { color: #67e8f9; margin-right: 6px; }
  @keyframes wnCheerIn { to { opacity: 1; transform: translateX(0) scale(1); } }

  /* Throw it / say it - player card, cinematic tomato, paid call-out */
  .wn-shakewrap { width: 100%; height: 100%; }
  .wn-shakewrap.on { animation: wnShake 520ms ease; }
  @keyframes wnShake {
    0%, 100% { transform: translate(0, 0); }
    15% { transform: translate(-10px, 4px) rotate(-0.6deg); }
    30% { transform: translate(9px, -3px) rotate(0.5deg); }
    45% { transform: translate(-7px, 2px); }
    60% { transform: translate(6px, -2px); }
    75% { transform: translate(-4px, 1px); }
    90% { transform: translate(2px, 0); } }
  .wn-profilecard { position: absolute; right: max(4vw, 18px); top: 24%; width: min(320px, 86vw); z-index: 7;
    background: linear-gradient(160deg, rgba(13,31,46,0.98), rgba(6,14,24,0.98));
    border: 1px solid rgba(212,175,55,0.55); border-radius: 16px; padding: 18px; text-align: left;
    box-shadow: 0 22px 60px rgba(0,0,0,0.65);
    opacity: 0; transform: translateX(30px); animation: wnCoach 480ms cubic-bezier(0.16,1,0.3,1) forwards; }
  .wn-profilecard .pc-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
  .wn-profilecard .pc-ava { width: 46px; height: 46px; border-radius: 50%; flex-shrink: 0;
    background: rgba(212,175,55,0.15); border: 2px solid rgba(212,175,55,0.5);
    display: flex; align-items: center; justify-content: center; font-size: 24px; }
  .wn-profilecard .pc-name { font-family: Georgia, serif; font-size: 17px; color: #fff; }
  .wn-profilecard .pc-stack { font-size: 12px; color: #8fa1b5; margin-top: 2px; }
  .wn-profilecard .pc-tags { margin-left: auto; display: flex; gap: 5px; }
  .wn-profilecard .pc-tags span { width: 14px; height: 14px; border-radius: 50%; display: block;
    border: 1px solid rgba(255,255,255,0.4); }
  .wn-profilecard .pc-stats { display: flex; gap: 8px; }
  .wn-profilecard .pc-stats div { flex: 1; background: rgba(255,255,255,0.05); border-radius: 8px;
    padding: 7px 0; text-align: center; }
  .wn-profilecard .pc-stats span { display: block; font-size: 9.5px; letter-spacing: 0.14em; color: #8fa1b5; }
  .wn-profilecard .pc-stats b { font-size: 14px; color: #fff; }
  .wn-profilecard .pc-label { font-size: 10px; letter-spacing: 0.26em; color: #d4af37; margin: 12px 0 7px; }
  .wn-profilecard .pc-row { display: flex; gap: 9px; }
  .wn-profilecard .pc-item { width: 52px; height: 52px; border-radius: 12px; background: rgba(255,255,255,0.06);
    border: 1px solid rgba(148,163,184,0.35); display: flex; align-items: center; justify-content: center;
    font-size: 26px; }
  .wn-profilecard .pc-item.hot { border-color: #d4af37; box-shadow: 0 0 18px rgba(212,175,55,0.45);
    animation: wnPulse 1.4s ease infinite; }
  .wn-profilecard .pc-callout { font-size: 12.5px; color: #fca5a5; background: rgba(248,113,113,0.08);
    border: 1px solid rgba(248,113,113,0.4); border-radius: 10px; padding: 9px 12px; }
  /* Names the player card while it sits open, so the feature reads as a
     feature rather than a step on the way to the throw. */
  .wn-cardcaption { position: absolute; left: max(4vw, 20px); bottom: 12vh; z-index: 8;
    width: min(430px, 52vw); background: linear-gradient(160deg, rgba(13,31,46,0.97), rgba(6,14,24,0.97));
    border: 1px solid rgba(103,232,249,0.5); border-left: 3px solid #67e8f9;
    border-radius: 12px; padding: 14px 16px; font-size: 13.5px; line-height: 1.55;
    color: #dbe4ee; box-shadow: 0 16px 44px rgba(0,0,0,0.55);
    opacity: 0; transform: translateY(16px);
    animation: wnFadeUp 480ms cubic-bezier(0.16,1,0.3,1) forwards; }
  .wn-cardcaption b { color: #67e8f9; letter-spacing: 0.12em; font-size: 11.5px;
    display: block; margin-bottom: 5px; }

  .wn-proj { position: absolute; left: 50%; top: 30%; z-index: 7; pointer-events: none;
    font-size: clamp(40px, 6vw, 62px); filter: drop-shadow(0 6px 16px rgba(0,0,0,0.6));
    animation: wnProjArc 880ms cubic-bezier(0.2, 0.7, 0.4, 1) forwards; }
  @keyframes wnProjArc {
    0% { transform: translate(-50%, calc(-50% + 44vh)) scale(0.7) rotate(0deg); }
    55% { transform: translate(calc(-50% + 8vw), calc(-50% + 13vh)) scale(1.35) rotate(400deg); }
    100% { transform: translate(-50%, -50%) scale(0.95) rotate(720deg); } }
  .wn-splatwrap { position: absolute; left: 50%; top: 30%; z-index: 6; pointer-events: none;
    transform: translate(-50%, -50%); }
  .wn-impactring { position: absolute; left: 50%; top: 50%; width: 96px; height: 96px; margin: -48px;
    border-radius: 50%; border: 3px solid rgba(255,120,80,0.9);
    animation: wnRipple 650ms ease-out forwards; }
  .wn-splat { width: 92px; height: 84px;
    background: radial-gradient(circle at 40% 35%, #ff6b57, #d92b1c 60%, #a31607);
    border-radius: 48% 62% 55% 45% / 55% 48% 60% 50%;
    box-shadow: 0 0 30px rgba(217,43,28,0.65);
    animation: wnSplatIn 420ms cubic-bezier(0.16,1,0.3,1), wnSplatFade 2600ms ease 2000ms forwards; }
  @keyframes wnSplatIn { from { transform: scale(0.2); opacity: 0.6; } 60% { transform: scale(1.25); }
    to { transform: scale(1); opacity: 1; } }
  @keyframes wnSplatFade { to { opacity: 0; } }
  .wn-splatdrip { position: absolute; top: 62px; width: 12px; height: 26px; border-radius: 8px;
    background: linear-gradient(180deg, #d92b1c, #a31607); opacity: 0.9;
    animation: wnDripFall 1400ms ease-in forwards; }
  .wn-splatdrip.d1 { left: 16px; animation-delay: 150ms; }
  .wn-splatdrip.d2 { left: 42px; animation-delay: 320ms; height: 34px; }
  .wn-splatdrip.d3 { left: 66px; animation-delay: 480ms; height: 22px; }
  @keyframes wnDripFall { from { transform: translateY(0) scaleY(0.4); }
    to { transform: translateY(48px) scaleY(1); opacity: 0; } }
  .wn-announce { position: absolute; top: 42%; left: 0; right: 0; z-index: 8; text-align: center;
    pointer-events: none; padding: 18px 12px;
    background: linear-gradient(90deg, transparent, rgba(8,12,20,0.94) 18%, rgba(8,12,20,0.94) 82%, transparent);
    border-top: 1px solid rgba(212,175,55,0.5); border-bottom: 1px solid rgba(212,175,55,0.5);
    animation: wnAnnIn 500ms cubic-bezier(0.16,1,0.3,1); }
  .wn-announce .from { font-size: 11px; letter-spacing: 0.3em; color: #d4af37; margin-bottom: 8px; }
  .wn-announce .msg { font-family: Georgia, serif; font-size: clamp(20px, 3.4vw, 34px); font-weight: 700;
    color: #fff; letter-spacing: 0.06em; text-shadow: 0 0 26px rgba(248,113,113,0.55);
    animation: wnAnnPulse 900ms ease 2 alternate; }
  @keyframes wnAnnIn { from { opacity: 0; transform: scaleX(0.3); } to { opacity: 1; transform: scaleX(1); } }
  @keyframes wnAnnPulse { from { transform: scale(1); } to { transform: scale(1.05); } }

  .wn-logo { width: clamp(72px, 10vw, 120px); height: clamp(72px, 10vw, 120px); object-fit: contain;
    margin-bottom: 26px; border-radius: 22%;
    filter: drop-shadow(0 6px 28px rgba(212,175,55,0.45));
    opacity: 0; transform: scale(0.6);
    animation: wnLogoIn 800ms cubic-bezier(0.16,1,0.3,1) 100ms forwards; }
  @keyframes wnLogoIn { to { opacity: 1; transform: scale(1); } }

  .wn-begin { margin-top: 32px; background: linear-gradient(135deg, #f5d976, #d4af37 55%, #a97f1f); color: #181205;
    border: none; border-radius: 28px; padding: 16px 52px; font-size: 15px; font-weight: 800;
    letter-spacing: 0.24em; cursor: pointer; box-shadow: 0 10px 40px rgba(212,175,55,0.5);
    animation: wnPulse 1.6s ease infinite; }
  @keyframes wnPulse { 0%,100% { transform: scale(1); box-shadow: 0 10px 30px rgba(212,175,55,0.4); }
    50% { transform: scale(1.05); box-shadow: 0 10px 52px rgba(212,175,55,0.75); } }
  .wn-audiostatus { margin-top: 18px; font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; }
  .wn-audiostatus.ok { color: #6ee7b7; }
  .wn-audiostatus.missing { color: #f87171; }
  .wn-ctas { display: flex; gap: 14px; margin-top: 34px; flex-wrap: wrap; justify-content: center;
    opacity: 0; animation: wnSubIn 600ms ease 1000ms forwards; }
  .wn-cta { border-radius: 22px; padding: 13px 30px; font-weight: 800; font-size: 14px;
    letter-spacing: 0.1em; cursor: pointer; border: 1px solid rgba(212,175,55,0.55);
    background: transparent; color: #fde68a; }
  .wn-cta.primary { background: linear-gradient(135deg, #f5d976, #d4af37 55%, #a97f1f); color: #181205; border: none; }

  /* ═══ PHONES / iOS ═══════════════════════════════════════════════════
     The tour was authored for a wide screen: side panels pinned right,
     controls bottom-right, page dots bottom-centre, and titles set with
     white-space: nowrap. On a phone the dots landed on top of the
     MUTE/NEXT/EXIT row, nothing cleared the notch or the home indicator,
     and the felt left the bottom half of a tall screen empty. */
  @media (max-width: 820px) {
    /* Clear the Dynamic Island / notch at the top. */
    .wn-chapterhead { top: calc(1.5vh + env(safe-area-inset-top)); }

    /* Long titles ("YOUR SEAT IS WAITING", "THE SOPHIA TABLE") ran off a
       narrow screen at the old 34px floor because they could not wrap. */
    .wn-title { white-space: normal; font-size: clamp(26px, 8.4vw, 46px);
      letter-spacing: 0.04em; margin: 12px 0; padding: 0 4vw;
      max-width: 100%; overflow-wrap: break-word; }

    /* The entrance animation ends at 0.2em tracking with fill-mode
       forwards, which wins over the rule above - on a phone that alone
       pushed a long title past both screen edges. Same animation, tighter
       landing, small screens only. */
    @keyframes wnTitleIn { to { opacity: 1; transform: scale(1); letter-spacing: 0.05em; } }

    /* POKERSWEEPSTARS is a single 15-character word: it cannot wrap and it
       cannot shrink to fit, so it needs its own smaller size or it bleeds
       off both edges of the splash. */
    .wn-brandtitle { font-size: clamp(20px, 6.3vw, 34px); letter-spacing: 0.02em; }
    .wn-chapterhead .wn-title { font-size: clamp(19px, 5.6vw, 30px); margin: 6px 0; }
    .wn-sub { font-size: clamp(10px, 3vw, 13px); letter-spacing: 0.16em; padding: 0 6vw; }
    .wn-chapterhead .wn-sub { font-size: clamp(9px, 2.5vw, 12px); }
    .wn-rule { width: min(78vw, 420px); }
    .wn-chapterhead .wn-rule { width: min(52vw, 300px); }
    .wn-logo { width: clamp(64px, 20vw, 100px); height: clamp(64px, 20vw, 100px); margin-bottom: 16px; }

    /* Dots ABOVE the buttons instead of sharing the same line, and both
       lifted clear of the home indicator / Safari's bottom bar. */
    .wn-dots { bottom: calc(60px + env(safe-area-inset-bottom)); }
    .wn-controls { bottom: calc(12px + env(safe-area-inset-bottom));
      left: 0; right: 0; justify-content: center; }
    .wn-btn { padding: 10px 20px; font-size: 12px; }

    /* Fill the portrait screen - a 70vh-tall, 1080px-wide felt left a
       black band across the bottom of a phone. */
    .wn-tablewrap { width: 100vw; height: 54vh; margin-top: 9vh; overflow: hidden; }
    .wn-backdrop-felt { width: 165vw; height: 62vh; transform: scale(1); }

    /* The app's portrait-table update sizes phone felts to the LIVE game
       screen (calc(100dvh - chrome)) - inside these demo frames that
       blew the table out of the slide. The classic felt reads the
       --pss-felt-h variable, so pin it to the frame; the premium
       (Sophia) felt needs a direct override. */
    .wn-tablewrap, .wn-backdrop-felt { --pss-felt-h: 100%; }
    .wn-tablewrap .premium-table-svg,
    .wn-backdrop-felt .premium-table-svg { height: 100% !important; max-height: 100% !important; }

    /* Side panels become bottom cards: a 300px panel pinned to the right
       edge covers the felt on a phone and reads badly. */
    .wn-coach, .wn-profilecard {
      left: 4vw; right: 4vw; width: auto; top: auto;
      bottom: calc(104px + env(safe-area-inset-bottom));
      max-height: 42vh; overflow-y: auto;
    }
    .wn-sophiacaption { top: auto; }
    .wn-profilecard .pc-item { width: 46px; height: 46px; font-size: 23px; }

    /* Scene furniture that assumed a wide viewport */
    .wn-actionbar { bottom: calc(78px + env(safe-area-inset-bottom)); gap: 10px; }
    .wn-act { padding: 13px 26px; font-size: 14px; }
    .wn-cheerbox { left: 4vw; right: 4vw; max-width: none;
      bottom: calc(104px + env(safe-area-inset-bottom)); }
    .wn-watchbadge { top: calc(9vh + env(safe-area-inset-top)); font-size: 11px; padding: 6px 14px; }
    .wn-lobbycard { margin-top: 16vh; padding: 24px 20px 22px; }
    .wn-scenegrid { margin-top: 15vh; gap: 12px; }
    .wn-scenecard { padding: 16px; }
    .wn-spinwrap { width: 74vw; height: 74vw; margin-top: 15vh; }
    .wn-spinwin { margin-top: 14px; }
    .wn-slots { gap: 9px; }
    .wn-reel { width: 27vw; height: 108px; }
    .wn-sym { height: 108px; font-size: 44px; }
    @keyframes wnSpin { from { transform: translateY(0); } to { transform: translateY(-324px); } }
    .wn-winflash { font-size: clamp(24px, 8vw, 44px); padding: 0 5vw; }
    .wn-announce .msg { font-size: clamp(17px, 5.6vw, 26px); }
    .wn-watchcode { font-size: 24px; letter-spacing: 0.24em; }

    /* Sophia's room needs more shrink to fit a phone frame. */
    .wn-sophia { transform: scale(0.62); transform-origin: center 28%; }

    /* Outro actions: full-width stack beats a wrapped row of pills. */
    .wn-ctas { flex-direction: column; width: 78vw; gap: 10px; margin-top: 22px; }
    .wn-cta { width: 100%; padding: 14px 0; }
    .wn-begin { padding: 15px 44px; }
  }

  /* Phones held sideways: vertical space is the scarce axis. */
  @media (max-height: 520px) and (orientation: landscape) {
    .wn-chapterhead { top: calc(0.5vh + env(safe-area-inset-top)); }
    .wn-chapterhead .wn-title { font-size: clamp(16px, 3.4vw, 24px); margin: 4px 0; }
    .wn-chapterhead .wn-sub { display: none; }
    .wn-tablewrap { height: 64vh; margin-top: 12vh; }
    .wn-logo { width: 56px; height: 56px; margin-bottom: 8px; }
    .wn-title { font-size: clamp(22px, 5vw, 38px); margin: 8px 0; }
    .wn-ctas { flex-direction: row; width: auto; }
    .wn-cta { width: auto; padding: 10px 22px; }
  }
`;

export default WhatsNewTourPage;
